import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectSeatRequests } from "../../features/sessions/seats-slice";
import { fetchSeatRequests } from "../../features/sessions/slice-extensions/seats-slice-extension";
import { formatSeatDateTime } from "../../features/sessions/utils/seatDateTime";
import { ROUTES } from "../../routes";
import SeatDecisionButtons from "../sessions/SeatDecisionButtons";

/**
 * Home card of open booking requests (Requested seats on upcoming dates), soonest first,
 * each with Confirm and Decline. Hidden until there is a request; once the instructor
 * clears the last one in this visit it shows the empty line instead of jumping away.
 */
const SeatRequestsCard = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const requests = useAppSelector(selectSeatRequests);
  const [hadRequests, setHadRequests] = useState(false);

  useEffect(() => {
    dispatch(fetchSeatRequests());
  }, [dispatch]);

  useEffect(() => {
    if (requests.length > 0) setHadRequests(true);
  }, [requests.length]);

  if (requests.length === 0 && !hadRequests) {
    return null;
  }

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10 lg:p-6">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-grey-20 dark:text-white">
        <CalendarClock size={20} />
        {t("home.seatRequests.title")}
      </h2>

      {requests.length === 0 ? (
        <p className="mt-3 text-sm text-grey-40 dark:text-grey-80">{t("home.seatRequests.empty")}</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {requests.map((request) => (
            <li
              key={request.seatId}
              className="flex items-center justify-between gap-3 rounded-xl bg-grey-95 px-3 py-2 dark:bg-primary-20"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-grey-20 dark:text-white">{request.memberName}</p>
                <p className="truncate text-xs text-grey-40 dark:text-grey-80">
                  <Link
                    to={ROUTES.SESSION_DETAILS.replace(":id", request.bookingId)}
                    className="cursor-pointer hover:underline"
                  >
                    {request.sessionTitle}
                  </Link>
                  {" · "}
                  {formatSeatDateTime(request.occurrenceDate, request.timeLocal, i18n.language)}
                </p>
              </div>
              <SeatDecisionButtons seat={request} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SeatRequestsCard;
